import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { sessionData } from "../helpers/session-data";
import { getEmployeeLoggedInBoolean } from "../helpers/employee-logged-in";
import { api } from "../config/api";
import { post } from "../api";
import type { RootState } from "../types/store";
import { selectViewerRoleContext } from "./authSelectors";
import type {
  DashboardMetrics,
  UserActivityItem,
} from "../components/MainContent/pages/Dashboard/types";

interface CaseStatItem {
  case_curr_state: string;
  total_count: number;
}

interface BusinessTeamStats {
  total_emp: number;
  total_logged_in_emp: number;
}

export interface DashboardState {
  metrics: DashboardMetrics;
  userActivity: UserActivityItem[];
  isLoading: boolean;
  error: string | null;
}

const initialMetrics: DashboardMetrics = {
  totalClients: 0,
  registeredClients: 0,
  unregisteredClients: 0,
  totalCases: 0,
  inProgressCases: 0,
  closedCases: 0,
  totalUsers: 0,
  activeUsers: 0,
  inactiveUsers: 0,
  pastDurationCasesCount: 0,
  pastDurationClientsCount: 0,
};

const initialState: DashboardState = {
  metrics: initialMetrics,
  userActivity: [],
  isLoading: false,
  error: null,
};

interface GetDashboardResponse {
  total_cases?: number;
  overall_case_status?: Record<string, number | string>;
  client_count_info?: {
    total_count: number;
    registered_clients: number;
    unregistered_clients: number;
  };
  case_owner_count?: Array<{ team_member: string; user_id: number; case_count: number }>;
}

function coerceNonNegativeNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) return Math.trunc(n);
  }
  return undefined;
}

function sumOverallCaseStatusCounts(
  status: Record<string, number | string> | undefined | null
): number {
  if (!status || typeof status !== "object") return 0;
  let sum = 0;
  for (const v of Object.values(status)) {
    const n = coerceNonNegativeNumber(v);
    if (n !== undefined) sum += n;
  }
  return sum;
}

function resolveTotalCasesFromDashboard(dashboard: GetDashboardResponse): number {
  const explicit =
    coerceNonNegativeNumber(dashboard.total_cases) ??
    coerceNonNegativeNumber((dashboard as { case_count?: unknown }).case_count) ??
    coerceNonNegativeNumber((dashboard as { cases_total?: unknown }).cases_total);
  if (explicit !== undefined) return explicit;
  const status = dashboard.overall_case_status;
  const statusTotal =
    status && typeof status === "object"
      ? (coerceNonNegativeNumber((status as Record<string, unknown>).total) ??
        coerceNonNegativeNumber((status as Record<string, unknown>).total_count))
      : undefined;
  if (statusTotal !== undefined) return statusTotal;
  return sumOverallCaseStatusCounts(status);
}

function isClientUserFromState(state: RootState): boolean {
  return selectViewerRoleContext(state).isClient;
}

function normalizeStatusKey(key: string): string {
  return key.toLowerCase().replace(/-/g, "_");
}

function statusBucketCount(
  status: Record<string, number | string> | undefined,
  key: string
): number {
  if (!status) return 0;
  const target = normalizeStatusKey(key);
  let best = 0;
  for (const [k, v] of Object.entries(status)) {
    if (normalizeStatusKey(k) === target) {
      best = Math.max(best, coerceNonNegativeNumber(v) ?? 0);
    }
  }
  return best;
}

function mapGetDashboardToMetrics(
  dashboard: GetDashboardResponse,
  teamStats: { totalUsers: number; activeUsers: number }
): DashboardMetrics {
  const status = dashboard.overall_case_status;
  const totalCases = resolveTotalCasesFromDashboard(dashboard);
  const inProgressCases = statusBucketCount(status, "in-progress");
  const closedCases = statusBucketCount(status, "closed");

  const clientInfo = dashboard.client_count_info;
  const totalClients = clientInfo?.total_count ?? 0;
  const registeredClients = clientInfo?.registered_clients ?? 0;
  const unregisteredClients = clientInfo?.unregistered_clients ?? 0;
  const totalUsers = teamStats.totalUsers || (dashboard.case_owner_count?.length ?? 0);

  return {
    totalClients,
    registeredClients,
    unregisteredClients,
    totalCases,
    inProgressCases,
    closedCases,
    totalUsers,
    activeUsers: teamStats.activeUsers,
    inactiveUsers: Math.max(0, totalUsers - teamStats.activeUsers),
    pastDurationCasesCount: totalCases,
    pastDurationClientsCount: totalClients,
  };
}

export const fetchDashboard = createAsyncThunk(
  "dashboard/fetchDashboard",
  async (_, { rejectWithValue, getState }) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id) {
      return rejectWithValue("Session not available");
    }

    if (isClientUserFromState(getState() as RootState)) {
      return { metrics: initialMetrics, userActivity: [] };
    }

    const body = { business_id, device_token, session_id };

    const getDashboardUrl = api.GET_DASHBOARD || api.GETDASHBOARD;

    const teamData = await post<{
      data?: { business_team_stats?: BusinessTeamStats; business_team?: UserActivityItem[] };
    }>(api.GET_BUSINESS_TEAM, body);
    const stats: BusinessTeamStats | undefined = teamData.data?.business_team_stats;
    const teamRaw = teamData.data?.business_team ?? [];
    const team: UserActivityItem[] = teamRaw.map((row) => {
      const r = row as unknown as Record<string, unknown>;
      const base = row as UserActivityItem;
      const missingUserId =
        r.user_id === undefined || r.user_id === null || String(r.user_id).trim() === "";
      const uid = missingUserId ? String(base.user_id ?? "") : String(r.user_id);
      const sessions = Array.isArray(r.active_sessions)
        ? r.active_sessions
        : (base.active_sessions ?? []);
      return {
        ...base,
        user_id: uid,
        username: typeof r.username === "string" ? r.username : base.username,
        email: typeof r.email === "string" ? r.email : base.email,
        is_active: typeof r.is_active === "boolean" ? r.is_active : base.is_active,
        active_sessions: sessions as UserActivityItem["active_sessions"],
        employee_loggedin: getEmployeeLoggedInBoolean(r),
      };
    });
    const sortedTeam = [...team].sort((a, b) =>
      (a.employee_loggedin ? 1 : 0) < (b.employee_loggedin ? 1 : 0) ? 1 : -1
    );
    const totalUsers = stats?.total_emp ?? 0;
    const activeUsers = stats?.total_logged_in_emp ?? 0;

    let metrics: DashboardMetrics;

    if (getDashboardUrl) {
      const dashboardPayload = await post<
        GetDashboardResponse & { data?: GetDashboardResponse } & Record<string, unknown>
      >(getDashboardUrl, body);
      let dashboardData: GetDashboardResponse =
        dashboardPayload.overall_case_status !== undefined &&
        dashboardPayload.overall_case_status !== null
          ? dashboardPayload
          : (dashboardPayload.data ?? {});

      const rootTotal = coerceNonNegativeNumber(dashboardPayload.total_cases);
      if (dashboardData.total_cases === undefined && rootTotal !== undefined) {
        dashboardData = { ...dashboardData, total_cases: rootTotal };
      }

      metrics = mapGetDashboardToMetrics(dashboardData, { totalUsers, activeUsers });
    } else {
      const [caseStatsData, clientsData] = await Promise.all([
        post<{ data?: { case_stats?: CaseStatItem[] } }>(api.GET_CASE_STATS, body),
        post<{
          data?: { clients?: unknown[]; pagination?: { total_clients?: number } };
        }>(api.GET_CLIENTS, body),
      ]);
      const clientsList = clientsData.data?.clients ?? [];
      const totalClients = clientsData.data?.pagination?.total_clients ?? clientsList.length;
      let totalCases = 0;
      let inProgressCases = 0;
      let closedCases = 0;
      const caseStats: CaseStatItem[] = caseStatsData.data?.case_stats ?? [];
      caseStats.forEach((item) => {
        totalCases += item.total_count ?? 0;
        const state = (item.case_curr_state || "").toUpperCase().replace(/-/g, "_");
        if (state === "IN PROGRESS" || state === "IN_PROGRESS") {
          inProgressCases += item.total_count ?? 0;
        } else if (state === "CLOSED" || state === "COMPLETED") {
          closedCases += item.total_count ?? 0;
        }
      });
      if (inProgressCases === 0 && closedCases === 0 && caseStats.length > 0) {
        inProgressCases = totalCases - closedCases;
      }
      metrics = {
        totalClients,
        registeredClients: totalClients,
        unregisteredClients: 0,
        totalCases,
        inProgressCases,
        closedCases,
        totalUsers,
        activeUsers,
        inactiveUsers: totalUsers - activeUsers,
        pastDurationCasesCount: totalCases,
        pastDurationClientsCount: totalClients,
      };
    }

    return { metrics, userActivity: sortedTeam };
  }
);

export type PastDurationValue =
  | "hour"
  | "daily"
  | "weekly"
  | "monthly"
  | "start_of_year"
  | "last_year"
  | "till_date";

export const fetchCaseCreationStatus = createAsyncThunk(
  "dashboard/fetchCaseCreationStatus",
  async (duration: PastDurationValue, { rejectWithValue }) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id) {
      return rejectWithValue("Session not available");
    }
    const url = api.GET_CASE_CREATION_STATUS;
    if (!url) return rejectWithValue("Case creation status API not configured");

    const data = await post<{
      data?: { case_count?: number; client_count?: number };
    }>(url, {
      business_id,
      device_token,
      session_id,
      time_frame: duration,
      from_date: "",
      till_date: "",
    });
    const casesCount = data?.data?.case_count ?? 0;
    const clientsCount = data?.data?.client_count ?? 0;
    return {
      pastDurationCasesCount: Number(casesCount),
      pastDurationClientsCount: Number(clientsCount),
    };
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    },
    resetDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchDashboard.fulfilled,
        (
          state,
          action: PayloadAction<{ metrics: DashboardMetrics; userActivity: UserActivityItem[] }>
        ) => {
          state.isLoading = false;
          state.metrics = action.payload.metrics;
          state.userActivity = action.payload.userActivity;
          state.error = null;
        }
      )
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) ?? action.error.message ?? "Failed to load dashboard";
      })
      .addCase(fetchCaseCreationStatus.fulfilled, (state, action) => {
        state.metrics.pastDurationCasesCount = action.payload.pastDurationCasesCount;
        state.metrics.pastDurationClientsCount = action.payload.pastDurationClientsCount;
      });
  },
});

export const { clearDashboardError, resetDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
